// src/modules/app-views/_apps/appcaup/dashboard/pages/Init/index.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { useMemo } from 'react';
import { Card, Col, Row, Statistic, Typography, theme } from 'antd';
import {
  AuditOutlined,
  CalendarOutlined,
  DollarOutlined,
  FileProtectOutlined,
  FunnelPlotOutlined,
  RiseOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import ReactApexChart from 'react-apexcharts';
import translateX from 'utils/translateX';

const { Title, Text, Link } = Typography;
const { useToken } = theme;

// *******************************************************************************
// MOCK DATA
// *******************************************************************************
const MOCK_STATS = {
  leadsThisMonth: 0,
  leadsTotal: 44,
  signedContracts: 5,
  signedContractsThisMonth: 5,
  conversionRate: '4,5%',
  collectedIncome: '0,00 €',
  collectedIncomeThisMonth: '0,00 €',
  activePipeline: '0,00 €',
  overdueFollowups: 0,
};

const MOCK_CHART_DATA = [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 5];
const MOCK_CHART_CATEGORIES = [
  'Jul 25', 'Ago 25', 'Set 25', 'Out 25', 'Nov 25', 'Dez 25',
  'Jan 26', 'Fev 26', 'Mar 26', 'Abr 26', 'Mai 26', 'Jun 26',
];

const MOCK_FUNNEL = [
  { label: 'new', value: 42, color: '#4f46e5' },
  { label: 'contacted', value: 0, color: '#6366f1' },
  { label: 'appointment_scheduled', value: 0, color: '#f97316' },
  { label: 'proposal_sent', value: 0, color: '#ef4444' },
  { label: 'signed', value: 2, color: '#22c55e' },
  { label: 'collected', value: 0, color: '#16a34a' },
  { label: 'lost', value: 0, color: '#dc2626' },
];

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const CaupDashboardInit = () => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 2. HOOKS & REFS
  // ─────────────────────────────────────────────────────────────────────────────
  const { token } = useToken();

  // ─────────────────────────────────────────────────────────────────────────────
  // 3. LOCAL VARIABLES I
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 4. LOCAL STATE
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 5. EFFECTS I
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 6. HANDLERS
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 7. LOCAL VARIABLES II
  // ─────────────────────────────────────────────────────────────────────────────
  const barChartOptions = useMemo(
    () => ({
      chart: { type: 'bar', toolbar: { show: false }, background: 'transparent' },
      plotOptions: { bar: { columnWidth: '45%', borderRadius: 4 } },
      colors: [token.colorPrimary],
      xaxis: {
        categories: MOCK_CHART_CATEGORIES,
        labels: { style: { colors: token.colorTextSecondary, fontSize: '12px' } },
        axisBorder: { show: false },
        axisTicks: { show: false },
      },
      yaxis: {
        labels: { style: { colors: token.colorTextSecondary }, formatter: v => Math.round(v) },
      },
      dataLabels: { enabled: false },
      grid: { borderColor: token.colorBorderSecondary, strokeDashArray: 4 },
      tooltip: { theme: token.colorBgBase === '#ffffff' ? 'light' : 'dark' },
    }),
    [token]
  );

  const barChartSeries = useMemo(
    () => [{ name: translateX('contracts'), data: MOCK_CHART_DATA }],
    []
  );

  const funnelOptions = useMemo(
    () => ({
      chart: { type: 'bar', toolbar: { show: false }, background: 'transparent' },
      plotOptions: { bar: { horizontal: true, barHeight: '60%', distributed: true, borderRadius: 4 } },
      colors: MOCK_FUNNEL.map(f => f.color),
      xaxis: {
        categories: MOCK_FUNNEL.map(f => translateX(f.label)),
        labels: { style: { colors: token.colorTextSecondary, fontSize: '12px' } },
        axisBorder: { show: false },
        axisTicks: { show: false },
      },
      yaxis: {
        labels: { style: { colors: token.colorText, fontSize: '12px' } },
      },
      dataLabels: {
        enabled: true,
        formatter: val => (val === 0 ? '' : val),
        style: { fontSize: '12px', fontWeight: 600 },
      },
      grid: { borderColor: token.colorBorderSecondary, strokeDashArray: 4 },
      legend: { show: false },
      tooltip: { theme: token.colorBgBase === '#ffffff' ? 'light' : 'dark' },
    }),
    [token]
  );

  const funnelSeries = useMemo(
    () => [{ data: MOCK_FUNNEL.map(f => f.value) }],
    []
  );

  // ─────────────────────────────────────────────────────────────────────────────
  // 8. EFFECTS II
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 9. UI
  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <div style={{ padding: '0 4px' }}>
      {/* Page header */}
      <div style={{ marginBottom: 24 }}>
        <Title level={3} style={{ margin: 0 }}>
          Dashboard
        </Title>
        <Text type="secondary">{translateX('business_summary')}</Text>
      </div>

      {/* KPI stat cards */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={12} sm={8} md={4}>
          <StatCard
            title={translateX('leads_this_month')}
            value={MOCK_STATS.leadsThisMonth}
            subtitle={`${MOCK_STATS.leadsTotal} ${translateX('totals')}`}
            icon={<FunnelPlotOutlined />}
            iconColor={token.colorPrimary}
          />
        </Col>
        <Col xs={12} sm={8} md={4}>
          <StatCard
            title={translateX('signed_contracts')}
            value={MOCK_STATS.signedContracts}
            subtitle={`+${MOCK_STATS.signedContractsThisMonth} ${translateX('this_month')}`}
            subtitleColor="#22c55e"
            icon={<FileProtectOutlined />}
            iconColor="#22c55e"
          />
        </Col>
        <Col xs={12} sm={8} md={4}>
          <StatCard
            title={translateX('conversion')}
            value={MOCK_STATS.conversionRate}
            subtitle={translateX('leads_to_signed_collected')}
            valueColor={token.colorWarning}
            icon={<RiseOutlined />}
            iconColor={token.colorWarning}
          />
        </Col>
        <Col xs={12} sm={8} md={4}>
          <StatCard
            title={translateX('collected_income')}
            value={MOCK_STATS.collectedIncome}
            subtitle={`${MOCK_STATS.collectedIncomeThisMonth} ${translateX('this_month')}`}
            valueColor={token.colorPrimary}
            icon={<DollarOutlined />}
            iconColor={token.colorPrimary}
          />
        </Col>
        <Col xs={12} sm={8} md={4}>
          <StatCard
            title={translateX('active_pipeline')}
            value={MOCK_STATS.activePipeline}
            subtitle={translateX('unclosed_leads')}
            valueColor={token.colorPrimary}
            icon={<AuditOutlined />}
            iconColor={token.colorPrimary}
          />
        </Col>
        <Col xs={12} sm={8} md={4}>
          <StatCard
            title={translateX('overdue_followups')}
            value={MOCK_STATS.overdueFollowups}
            subtitle={
              <Link style={{ fontSize: 12 }} onClick={() => {}}>
                {translateX('review')} →
              </Link>
            }
            valueColor={token.colorError}
            icon={<WarningOutlined />}
            iconColor={token.colorError}
          />
        </Col>
      </Row>

      {/* Charts row */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} lg={16} style={{ display: 'flex', flexDirection: 'column' }}>
          <Card style={{ flex: 1 }}>
            <Title level={5} style={{ marginTop: 0, marginBottom: 16 }}>
              {translateX('contracts_last_12_months')}
            </Title>
            <ReactApexChart
              type="bar"
              series={barChartSeries}
              options={barChartOptions}
              height={280}
            />
          </Card>
        </Col>

        <Col xs={24} lg={8} style={{ display: 'flex', flexDirection: 'column' }}>
          <Card style={{ flex: 1 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <Title level={5} style={{ margin: 0 }}>
                {translateX('leads_funnel')}
              </Title>
              <Link style={{ fontSize: 13 }} onClick={() => {}}>
                {translateX('view_all')} →
              </Link>
            </div>
            <ReactApexChart
              type="bar"
              series={funnelSeries}
              options={funnelOptions}
              height={280}
            />
          </Card>
        </Col>
      </Row>

      {/* Bottom info cards */}
      <Row gutter={[16, 16]}>
        <Col xs={24} md={12}>
          <Card>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <Title level={5} style={{ margin: 0 }}>
                {translateX('upcoming_visits')}
              </Title>
              <Link style={{ fontSize: 13 }} onClick={() => {}}>
                {translateX('calendar')} →
              </Link>
            </div>
            <div style={{ textAlign: 'center', padding: '24px 0' }}>
              <Text type="secondary">{translateX('no_visits_next_7_days')}</Text>
            </div>
          </Card>
        </Col>

        <Col xs={24} md={12}>
          <Card>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <Title level={5} style={{ margin: 0 }}>
                {translateX('upcoming_followups')}
              </Title>
              <Link style={{ fontSize: 13 }} onClick={() => {}}>
                CRM →
              </Link>
            </div>
            <div style={{ textAlign: 'center', padding: '24px 0' }}>
              <Text type="secondary">{translateX('no_followups_next_7_days')}</Text>
            </div>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default CaupDashboardInit;

// *******************************************************************************
// HELPERS
// *******************************************************************************
function StatCard({ title, value, subtitle, valueColor, subtitleColor, icon, iconColor }) {
  const { token } = useToken();

  return (
    <Card style={{ height: '100%' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div style={{ flex: 1 }}>
          <Text type="secondary" style={{ fontSize: 13, display: 'block', marginBottom: 4 }}>
            {title}
          </Text>
          <Statistic
            value={value}
            valueStyle={{ fontSize: 24, fontWeight: 700, color: valueColor || token.colorText, lineHeight: 1.2 }}
            formatter={v => v}
          />
          {subtitle && (
            <div style={{ marginTop: 4 }}>
              {typeof subtitle === 'string' ? (
                <Text style={{ fontSize: 12, color: subtitleColor || token.colorTextSecondary }}>
                  {subtitle}
                </Text>
              ) : (
                subtitle
              )}
            </div>
          )}
        </div>
        <div
          style={{
            width: 40,
            height: 40,
            borderRadius: 8,
            backgroundColor: iconColor ? `${iconColor}18` : `${token.colorPrimary}18`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 18,
            color: iconColor || token.colorPrimary,
            flexShrink: 0,
          }}
        >
          {icon}
        </div>
      </div>
    </Card>
  );
}
